import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { VOCCourseModel } from 'src/app/core/VOCCourseModel';
import { ShortCourseOrderModel } from 'src/app/core/ShortCourseOrderModel';
import { OrderService } from 'src/app/services/order/order.service';

interface PendingVocPayment {
  orderId: number;
  jobNumber: string;
  studentEmail: string;
  totalAmount: number;
  currencyCode: string;
}

@Component({
  selector: 'app-vocpayment',
  templateUrl: './vocpayment.component.html',
  styleUrl: './vocpayment.component.scss'
})
export class VOCPaymentComponent implements OnInit {

  // Store course fee
  courseFee = 0;

  // Store VOC course details
  vocDetails: VOCCourseModel | null = null;

  // Store job number
  jobNumber = '';

  // Store entered email address
  enteredEmailAddress = '';

  // Store decoded URL email
  private encodedUrlEmail = '';

  // Track matching state
  isMatching = false;

  // Track searching state
  isSearching = false;

  // Track checkout creation state
  isCreatingCheckout = false;

  // Store error message
  errorMessage = '';

  // Store success message
  successMessage = '';

  // Store payment success message
  paymentSuccessMessage = '';

  // Store Read and decode the email query parameter.   
  isEmailReadOnly = false;

  constructor(
    private readonly orderService: OrderService,
    private readonly route: ActivatedRoute
  ) { }

  // Initialise VOC payment details
  ngOnInit(): void {

    // Read email from URL
    this.readEmailFromUrl();

    // Check Stripe return
    this.checkStripeReturn();
  }


  // Read email from URL
  private readEmailFromUrl(): void {

    // Get encoded email
    const encodedEmail =
      this.route.snapshot.queryParamMap.get('email');

    // Handle missing email
    if (!encodedEmail) {

      this.isEmailReadOnly = false;

      return;
    }

    // Decode URL email
    this.encodedUrlEmail =
      this.decodeEmail(encodedEmail);

    // Store entered email
    this.enteredEmailAddress =
      this.encodedUrlEmail;

    // Set email readonly state
    this.isEmailReadOnly =
      !!this.encodedUrlEmail;
  }


  // Decode URL email
  private decodeEmail(encodedEmail: string): string {

    try {

      // Convert to Base64 format
      let base64 = encodedEmail
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      // Add Base64 padding
      while (base64.length % 4 !== 0) {
        base64 += '=';
      }

      // Decode Base64 value
      const binaryValue = atob(base64);

      // Convert decoded value to bytes
      const bytes = Uint8Array.from(
        binaryValue,
        character => character.charCodeAt(0)
      );

      // Return decoded email
      return new TextDecoder().decode(bytes);

    } catch (error) {

      console.error(
        'Unable to decode email from URL.',
        error
      );

      return '';
    }
  }


  // Normalize email value
  private normalizeEmail(value?: string | null): string {

    return (value ?? '')
      .trim()
      .toLowerCase();
  }


  // Verify email address
  private verifyEmail(vocDetails: VOCCourseModel): boolean {

    // Store entered email
    const enteredEmail =
      this.normalizeEmail(this.enteredEmailAddress);

    // Store URL email
    const urlEmail =
      this.normalizeEmail(this.encodedUrlEmail);

    // Store registered email
    const registeredEmail =
      this.normalizeEmail(vocDetails.clientEmail);

    // Validate required email values
    if (!enteredEmail || !registeredEmail) {
      return false;
    }

    // Verify URL and registered emails
    if (urlEmail) {

      return (
        enteredEmail === urlEmail &&
        enteredEmail === registeredEmail
      );
    }

    // Verify registered email
    return enteredEmail === registeredEmail;
  } // Reset email verification
  resetEmailVerification(): void {

    // Reset entered email
    this.enteredEmailAddress = '';

    // Reset VOC details
    this.vocDetails = null;

    // Reset course fee
    this.courseFee = 0;

    // Reset matching state
    this.isMatching = false;

    // Reset success message
    this.successMessage = '';

    // Reset error message
    this.errorMessage = '';
  }


  // Search VOC details
  searchVOCDetails(): void {

    // Store entered job number
    const enteredJobNumber =
      this.jobNumber.trim();

    // Store entered email
    const enteredEmail =
      this.enteredEmailAddress.trim();

    // Validate job number
    if (!enteredJobNumber) {

      this.errorMessage =
        'Please enter your job number.';

      return;
    }

    // Validate email address
    if (!enteredEmail) {

      this.errorMessage =
        'Please enter your email address.';

      return;
    }

    // Start search
    this.beginSearch();

    this.orderService
      .getVOCDetails(enteredJobNumber)
      .subscribe({

        next: response => {

          // Stop searching
          this.isSearching = false;

          // Handle missing VOC details
          if (!response) {

            this.errorMessage =
              'No VOC request was found for this job number or email address.';

            return;
          }

          // Store VOC details
          this.vocDetails = response;

          // Store course fee
          this.courseFee =
            Number(response.courseFee ?? 0);

          // Verify email
          this.isMatching =
            this.verifyEmail(response);

          // Stop when email does not match
          if (!this.isMatching) {
            return;
          }

          // Validate course fee
          if (this.courseFee <= 0) {

            this.errorMessage =
              'A payment amount has not been assigned to this VOC request yet.';

            return;
          }

          // Store success message
          this.successMessage =
            'VOC request found successfully.';
        },

        error: error => {

          // Stop searching
          this.isSearching = false;

          // Handle not found response
          if (error?.status === 404) {

            this.errorMessage =
              'No VOC request was found for this job number.';

          } else {

            // Store search error
            this.errorMessage =
              error?.error?.message ||
              'Unable to retrieve the VOC request details.';
          }
        }
      });
  }


  // Reset search values
  private beginSearch(): void {

    // Start searching
    this.isSearching = true;

    // Reset matching state
    this.isMatching = false;

    // Reset messages
    this.errorMessage = '';
    this.successMessage = '';
    this.paymentSuccessMessage = '';

    // Reset VOC details
    this.vocDetails = null;

    // Reset course fee
    this.courseFee = 0;
  }


  // Check payment completion
  isPaymentCompleted(): boolean {

    return (
      this.vocDetails?.paymentStatus
        ?.trim()
        .toLowerCase() === 'paid'
    );
  }

  // Create Stripe checkout session
  payNow(): void {

    // Validate VOC request
    if (!this.vocDetails || !this.isMatching) {

      this.errorMessage =
        'Please verify your VOC request first.';

      return;
    }

    // Check completed payment
    if (this.isPaymentCompleted()) {

      this.errorMessage =
        'This VOC request has already been paid.';

      return;
    }

    // Validate course fee
    if (this.courseFee <= 0) {

      this.errorMessage =
        'A valid course fee is required before payment.';

      return;
    }

    // Create checkout request
    const request =
      this.createCheckoutRequest();

    // Store pending payment
    this.storePendingPayment();

    // Start checkout creation
    this.isCreatingCheckout = true;

    // Reset messages
    this.errorMessage = '';
    this.successMessage = '';

    this.orderService
      .createStripeShortCheckout(request)
      .subscribe({

        next: response => {

          // Stop checkout creation
          this.isCreatingCheckout = false;

          // Validate checkout URL
          if (!response?.checkoutUrl) {

            this.errorMessage =
              'Stripe checkout URL was not returned.';

            return;
          }

          // Redirect to Stripe checkout
          window.location.href =
            response.checkoutUrl;
        },

        error: error => {

          // Stop checkout creation
          this.isCreatingCheckout = false;

          // Store checkout error
          this.errorMessage =
            error?.error?.message ||
            'Stripe checkout failed. Please try again.';
        }
      });
  }


  // Create checkout request
  private createCheckoutRequest(): ShortCourseOrderModel {

    // Store VOC details
    const vocDetails = this.vocDetails!;

    // Build checkout request
    return {
      id: vocDetails.id,
      vetTrackId: 0,
      shortCourseTitle: 'VOC Course Payment',
      studentGivenName: '',
      studentSingleName: '',
      studentEmail: vocDetails.clientEmail || '',
      totalAmount: this.courseFee,
      currencyCode: 'AUD',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      paymentStatus: 'Pending',
      createdDate: null,
      noOfSeatedLeft: 0,
      type: 'VOC',
      jobNo:
        vocDetails.jobNumber ||
        this.jobNumber.trim(),
      startDate: null,
      endDate: null,
      duration: ''
    };
  }


  // Store pending payment
  private storePendingPayment(): void {

    // Store VOC details
    const vocDetails = this.vocDetails!;

    // Create pending payment data
    const pendingPayment: PendingVocPayment = {
      orderId: vocDetails.id,
      jobNumber:
        vocDetails.jobNumber ||
        this.jobNumber.trim(),
      studentEmail:
        vocDetails.clientEmail || '',
      totalAmount: this.courseFee,
      currencyCode: 'AUD'
    };

    // Store payment in session
    sessionStorage.setItem(
      'ecb_pending_booking',
      JSON.stringify(pendingPayment)
    );
  }


  // Check Stripe return
  private checkStripeReturn(): void {

    // Get Stripe session ID
    const sessionId =
      this.route.snapshot.queryParamMap.get(
        'session_id'
      );

    // Check session ID
    if (!sessionId) {
      return;
    }

    // Get payment type
    const type =
      this.route.snapshot.queryParamMap.get('type');

    // Validate VOC payment type
    if (type?.toUpperCase() !== 'VOC') {
      return;
    }

    // Get pending payment data
    const rawPendingPayment =
      sessionStorage.getItem(
        'ecb_pending_booking'
      );

    // Handle missing pending payment
    if (!rawPendingPayment) {

      this.paymentSuccessMessage =
        'Your payment was completed successfully.';

      return;
    }

    try {

      // Parse pending payment data
      const pendingPayment =
        JSON.parse(
          rawPendingPayment
        ) as PendingVocPayment;

      // Store job number
      this.jobNumber =
        pendingPayment.jobNumber || '';

      // Store email address
      this.enteredEmailAddress =
        pendingPayment.studentEmail || '';

      // Store payment success message
      this.paymentSuccessMessage =
        'Your payment was completed successfully.';

    } catch (error) {

      console.error(
        'Unable to read pending VOC payment.',
        error
      );

    } finally {

      // Remove pending payment data
      sessionStorage.removeItem(
        'ecb_pending_booking'
      );
    }
  }
}